import {
  CreationField,
  CreationFieldLabel,
  CreationInputLengths,
  CreationRole,
  CreditsRole,
} from "./types";

export const RESEARCH_CREATION = "research-creation";

export const URLS = {
  RESEARCH_CREATION_ROOT: `/settings/${RESEARCH_CREATION}`,
  RESEARCH_CREATION_NEW: `/settings/${RESEARCH_CREATION}/new`,
  RESEARCH_CREATION_EDIT: `/settings/${RESEARCH_CREATION}/edit`,
};

export const creationFormItems: CreationField[] = [
  "title",
  "url",
  "title_in_english",
  "media",
  "duration_in_seconds",
  "premiere_venue",
  "premiere_venue_type",
  "premiere_city_country",
  "premiere_year",
  "non_premiere_1_venue",
  "non_premiere_1_venue_type",
  "non_premiere_1_city_country",
  "non_premiere_1_year",
  "non_premiere_2_venue",
  "non_premiere_2_venue_type",
  "non_premiere_2_city_country",
  "non_premiere_2_year",
  "_researchers_to_add_or_edit",
  "description",
  "creator",
  "director",
  "composer",
  "editor",
  "curator",
  "producer",
  "performers",
  "writers",
  "dramaturg",
  "cinematographer",
  "director_of_photography",
  "technical_director",
  "lighting_director",
  "set_designer",
  "video_production",
  "costume_designer",
  "installation_designer",
  "sound_designer",
  "production_designer",
  "production_engineer",
  "stage_manager",
  "distributor",
  "additional_credits",
];

export const requiredCreationField: CreationField[] = [
  "title",
  "media",
  "premiere_venue",
  "premiere_city_country",
  "premiere_year",
  "_researchers_id",
  "_researchers_roles",
  "description",
];

export const creationPlaceholders: Record<CreationField, string | undefined> = {
  title: "Specify in text box",
  url: "Specify in text box, if applicable",
  title_in_english: "Specify English translation, if applicable",
  media:
    "eg. Multi-media installation or Performance or Two-channel video installation or Generative music for electronics and live performer, or Recording",
  duration_in_seconds: undefined,
  premiere_venue:
    'Specify the venue/biennial/festival/exhibition, and specify "premiere" if applicable',
  premiere_venue_type:
    "solo exhibition; group exhibition, international dance festival, etc",
  premiere_city_country: 'Specify in the format "City, Country"',
  premiere_year: undefined,
  non_premiere_1_venue: undefined,
  non_premiere_1_venue_type: "solo exhibition; group exhibition etc",
  non_premiere_1_city_country: 'Specify in the format "City, Country"',
  non_premiere_1_year: undefined,
  non_premiere_2_venue: undefined,
  non_premiere_2_venue_type: "solo exhibition; group exhibition etc",
  non_premiere_2_city_country: 'Specify in the format "City, Country"',
  non_premiere_2_year: undefined,
  researchers_list: undefined,
  _researchers_to_add_or_edit: undefined,
  _researchers_id: "First Name Last Name",
  _researchers_roles: "drop-down menu; can choose more than one role",
  description: undefined,
  creator: "Specify in text box (First Name Last Name), if applicable",
  director: "Specify in text box, if applicable",
  composer: "Specify in text box, if applicable",
  editor: "Specify in text box, if applicable",
  curator: "Specify in text box, if applicable",
  producer: "Specify in text box, if applicable",
  performers: "Specify in text box, if applicable, with names and instruments",
  writers: "Specify in text box, if applicable",
  dramaturg: "Specify in text box, if applicable",
  cinematographer: "Specify in text box, if applicable",
  director_of_photography: "Specify in text box, if applicable",
  technical_director: "Specify in text box, if applicable",
  lighting_director: "Specify in text box, if applicable",
  set_designer: "Specify in text box, if applicable",
  video_production: "Specify in text box, if applicable",
  costume_designer: "Specify in text box, if applicable",
  installation_designer: "Specify in text box, if applicable",
  sound_designer: "Specify in text box, if applicable",
  production_designer: "Specify in text box, if applicable",
  production_engineer: "Specify in text box, if applicable",
  stage_manager: "Specify in text box, if applicable",
  distributor: "Specify in text box, if applicable",
  additional_credits:
    "Specify in text box, if applicable; might include assistants, other participants and/or supporters, funders etc",
};

export const creationMap: Record<CreationField, CreationFieldLabel> = {
  title: "Title",
  url: "URL",
  title_in_english: "Title in English",
  media: "Media",
  duration_in_seconds: "Duration",
  premiere_venue: "Premiere",
  premiere_venue_type: "Type of Venue",
  premiere_city_country: "City and Country of Presentation",
  premiere_year: "Year of Presentation",
  non_premiere_1_venue: "Venue #1",
  non_premiere_1_venue_type: "Type of Venue",
  non_premiere_1_city_country: "City and Country of Presentation",
  non_premiere_1_year: "Year of Presentation",
  non_premiere_2_venue: "Venue #2",
  non_premiere_2_venue_type: "Type of Venue",
  non_premiere_2_city_country: "City and Country of Presentation",
  non_premiere_2_year: "Year of Presentation",
  researchers_list: "SFU Faculty Collaborators",
  _researchers_to_add_or_edit: "Researchers to Add or Edit",
  _researchers_id: "Name",
  _researchers_roles: "Role(s)",
  description: "Description",
  creator: "Creator",
  director: "Director",
  composer: "Composer",
  editor: "Editor",
  curator: "Curator",
  producer: "Producer",
  performers: "Performer(s)",
  writers: "Writer(s)",
  dramaturg: "Dramaturg",
  cinematographer: "Cinematographer",
  director_of_photography: "Director of Photography",
  technical_director: "Technical Director",
  lighting_director: "Lighting Director",
  set_designer: "Set Designer",
  video_production: "Video Production",
  costume_designer: "Costume Designer",
  installation_designer: "Installation Designer",
  sound_designer: "Sound Designer",
  production_designer: "Production Designer",
  production_engineer: "Production Engineer",
  stage_manager: "Stage Manager",
  distributor: "Distributor",
  additional_credits: "Additional Credits",
};

export const INPUT_MAX_LENGTH: CreationInputLengths = {
  title: 500,
  url: 200,
  title_in_english: 500,
  media: 500,
  premiere_venue: 500,
  premiere_venue_type: 100,
  premiere_city_country: 100,
  non_premiere_1_venue: 500,
  non_premiere_1_venue_type: 100,
  non_premiere_1_city_country: 100,
  non_premiere_2_venue: 500,
  non_premiere_2_venue_type: 100,
  non_premiere_2_city_country: 100,
  description: 2000,
  creator: 100,
  director: 100,
  composer: 100,
  editor: 100,
  curator: 100,
  producer: 100,
  performers: 200,
  writers: 200,
  dramaturg: 100,
  cinematographer: 100,
  director_of_photography: 100,
  technical_director: 100,
  lighting_director: 100,
  set_designer: 100,
  video_production: 100,
  costume_designer: 100,
  installation_designer: 100,
  sound_designer: 100,
  production_designer: 100,
  production_engineer: 100,
  stage_manager: 100,
  distributor: 100,
  additional_credits: 2000,
};

export const creationRoles: CreationRole[] = [
  "Creator",
  "Co-creator",
  "Director",
  "Composer",
  "Editor",
  "Artist",
  "Artistic Director",
  "Choreographer",
  "Production Designer",
  "Sound Designer",
  "Curator",
  "Other [Specify in text box and press Enter]",
];

export const creationRolesDelimiter = ";";

export const creditsRoles: CreditsRole[] = [
  "creator",
  "director",
  "composer",
  "editor",
  "curator",
  "producer",
  "performers",
  "writers",
  "dramaturg",
  "cinematographer",
  "director_of_photography",
  "technical_director",
  "lighting_director",
  "set_designer",
  "video_production",
  "costume_designer",
  "installation_designer",
  "sound_designer",
  "production_designer",
  "production_engineer",
  "stage_manager",
  "distributor",
];

export const MESSAGE_DURATION = 5;

export const COUNT_PER_PAGE = 10;
